import { TFunction } from 'i18next';
import { FormikValues } from 'formik';

import {
  Resource,
  FormBlock,
  FormField,
  FormFieldType,
  FormSubmissionData,
} from 'types';

import { normalizeChoiceResponseValue } from './choice-response';
import { generateFormFieldsValidationSchema } from './validation';

export const getInitialValuesForFields = (
  fields: FormBlock['fields'],
  submissionData: FormSubmissionData | null
) => {
  let initialValues: FormikValues = {};

  fields.forEach((field) => {
    if (
      field.type === FormFieldType.PAGE_BREAK ||
      field.type === FormFieldType.HEADING
    ) {
      return;
    }

    if (field.type === FormFieldType.SCALE_RATING) {
      initialValues[field.id] =
        submissionData?.[field.id] ??
        field.options?.options?.map(() => null) ??
        [];
    } else if (field.type === FormFieldType.FIELD_ARRAY) {
      if (field.options?.fields) {
        initialValues = {
          ...initialValues,
          ...getInitialValuesForFields(field.options?.fields, submissionData),
        };
      }
    } else if (
      field.type === FormFieldType.CHECKBOX ||
      (field.type === FormFieldType.SELECT && !!field.options?.multiple)
    ) {
      initialValues[field.id] = normalizeChoiceResponseValue(
        submissionData?.[field.id] ?? []
      );
    } else if (
      field.type === FormFieldType.SELECT ||
      field.type === FormFieldType.RADIO
    ) {
      initialValues[field.id] = normalizeChoiceResponseValue(
        submissionData?.[field.id] ?? null
      );
    } else {
      initialValues[field.id] = submissionData?.[field.id] ?? '';
    }
  });
  return initialValues;
};

export const getInitialValuesForForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => getInitialValuesForFields(fields, submissionData);

export const getDataForFormikForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => {
  return {
    validationSchema: generateFormFieldsValidationSchema(fields),
    initialValues: getInitialValuesForForm(fields, submissionData),
  };
};

export const getResourceDuration = (resource: Resource): string => {
  const duration = resource.data.blocks.reduce(
    (total, block) => total + block.duration,
    0
  );
  const minutes = Math.ceil(duration / 60);

  return `${minutes} min${minutes !== 1 ? 's' : ''}`;
};

export const fixSubmissionTypes = (
  payload: FormSubmissionData,
  fields: FormField[]
): FormSubmissionData => {
  let newPayload = { ...payload };
  fields.forEach(({ id, type, options }) => {
    const isFieldArray = type === FormFieldType.FIELD_ARRAY;
    if (
      newPayload[id] !== undefined &&
      newPayload[id] !== null &&
      !isFieldArray
    ) {
      if (type === FormFieldType.INPUT && options?.type === 'number') {
        newPayload[id] = Number(newPayload[id]);
      }
    } else if (isFieldArray && options?.fields) {
      newPayload = fixSubmissionTypes(newPayload, options.fields);
    }
  });
  return newPayload;
};

export const booleanOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export const getTranslatedBooleanOptions = (t: TFunction) => [
  { value: 'true', label: t('yes') },
  { label: t('no'), value: 'false' },
];

export const splitFieldsByPages = (fields: FormField[]) =>
  fields.reduce(
    (pages, field) => {
      if (field.type === FormFieldType.PAGE_BREAK) {
        // Start a new page
        pages.push([]);
      } else {
        // Add field to current page
        if (pages.length === 0) pages.push([]); // Ensure at least one page exists
        pages[pages.length - 1].push(field);
      }
      return pages;
    },
    [] as Array<Array<FormField>>
  );
